import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggleButton from '../components/ThemeToggleButton';
import { useWebNotification } from '../hooks/useWebNotification';
import axios from 'axios';
import HandlerChat from './HandlerChat';
import HandlerProfile from './HandlerProfile';
import { toast } from 'sonner';
import StatusManagement from '../components/StatusManagement';
import Reports from '../components/settings/Reports';
import CallingAgentReports from '../components/settings/CallingAgentReports';
import CreateClient from '../components/settings/CreateClient';
import ClientsDirectory from '../components/settings/ClientsDirectory';
import Appearance from '../components/settings/Appearance';
import ServiceTimelines from '../components/settings/ServiceTimelines';
import { maskPhone, canViewFullPhone } from '../utils/phoneMask';
import { 
  MagnifyingGlass, 
  DotsThreeVertical,
  UserCircle,
  Chats,
  TrendUp,
  SignOut,
  Gear,
  Users,
  Megaphone,
  UsersThree,
  ArrowLeft,
  Star,
  ArrowClockwise,
  Copy,
  WhatsappLogo,
  Image as ImageIcon,
  Flag,
  Archive,
  Wrench,
  CaretDown,
  CaretUp,
  CaretRight,
  Eye,
  Phone,
  UserPlus,
  Alarm,
  FileText,
  Folder,
  ChartLineUp,
  ChatCenteredDots,
  Shield,
  Palette,
  ChartBar
} from '@phosphor-icons/react';
import BlockedClientsSection from '../components/BlockedClientsSection';
import MBSetupBadge, { MB_SETUPS, useAvailableMBs } from '../components/MBSetupBadge';
import BadgeDetailsView from '../components/BadgeDetailsView';
import BulkAssignMBModal from '../components/BulkAssignMBModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { startPolling, onVisibilityFetch } from '../utils/pollingKeepAlive';
import { connectSocket, onNewMessage, offNewMessage, onCustomerRegistered, offCustomerRegistered } from '../utils/socket';
import useResizableSidebar from '../hooks/useResizableSidebar';
import ResizeHandle from '../components/ResizeHandle';

const DEFAULT_SETTINGS_POWERS = {
  calling_agent_reports: true,
  create_client: true,
  inactive_clients: true,
  reports: true,
  client_directory: true,
  client_list: true,
  status: true,
  inquiry_chats: true,
  blocked_clients: true,
  appearance: false,
  service_timelines: false,
  mobile_number_full: false,
  enquiry_handler_mb: true
};

const HANDLER_SETTINGS_CARDS = [
  { id: 'calling_agent_reports', title: 'Calling Agent Reports', desc: 'Call/WhatsApp activity · sent reports', icon: Phone, color: 'text-pink-500', bg: 'bg-pink-50', powerKey: 'calling_agent_reports' },
  { id: 'create_client', title: 'Create Client Account', desc: 'Add new customer account', icon: UserPlus, color: 'text-green-500', bg: 'bg-green-50', powerKey: 'create_client' },
  { id: 'inactive_clients', title: 'Inactive Clients Tracker', desc: 'PDF Export · Inactive clients list', icon: Alarm, color: 'text-red-500', bg: 'bg-red-50', powerKey: 'inactive_clients' },
  { id: 'reports', title: 'Deposit / Withdraw Reports', desc: 'Excel · PDF · MB-wise reports', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', powerKey: 'reports' },
  { id: 'client_directory', title: 'Clients Directory', desc: 'MB-wise · WhatsApp Broadcast', icon: Folder, color: 'text-cyan-500', bg: 'bg-cyan-50', powerKey: 'client_directory' },
  { id: 'status', title: 'My Status Management', desc: 'Publish status updates & stories', icon: ChartLineUp, color: 'text-emerald-600', bg: 'bg-emerald-50', powerKey: 'status' },
  { id: 'blocked_clients', title: 'Blocked Clients', desc: 'Manage blocked customer list', icon: Shield, color: 'text-red-600', bg: 'bg-red-50', powerKey: 'blocked_clients' },
  { id: 'appearance', title: 'Appearance', desc: 'Theme & display preferences', icon: Palette, color: 'text-purple-500', bg: 'bg-purple-50', powerKey: 'appearance' },
  { id: 'service_timelines', title: 'Service Timelines', desc: 'Deposit · Withdraw · Support requests', icon: ChartBar, color: 'text-blue-500', bg: 'bg-blue-50', powerKey: 'service_timelines' }
];

const HandlerDashboard = () => {
  const { sidebarWidth, isDragging, startDragging } = useResizableSidebar(380, 260, 650);
  const { user, logout, updateUser, permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSubSetting, setActiveSubSetting] = useState(null);
  const [settingsSearch, setSettingsSearch] = useState('');
  const [badges, setBadges] = useState({ pw: 0, n: 0, g: 0, pt: 0, groups_pending: {} });
  const [showGroupsMenu, setShowGroupsMenu] = useState(false);
  const [showGBarMenu, setShowGBarMenu] = useState(false);
  const [showNMenu, setShowNMenu] = useState(false);
  const [nList, setNList] = useState([]);
  const [loadingNList, setLoadingNList] = useState(false);
  const [showPtMenu, setShowPtMenu] = useState(false);
  const [ptList, setPtList] = useState([]);
  const [loadingPtList, setLoadingPtList] = useState(false);
  const [ptThreshold, setPtThreshold] = useState(0);
  const [showPtThresholdMenu, setShowPtThresholdMenu] = useState(false);
  const [customPtMin, setCustomPtMin] = useState('');
  const ptMenuRef = useRef(null);
  const [activeBadgeView, setActiveBadgeView] = useState(null);
  const groupsMenuRef = useRef(null);
  const gBarMenuRef = useRef(null);
  const nMenuRef = useRef(null);
  const { showNotification, playNotificationSound, updateAppBadge } = useWebNotification();
  const availableMBs = useAvailableMBs();
  const [livePowers, setLivePowers] = useState(user?.settings_powers || null);
  const [liveAssignedMbs, setLiveAssignedMbs] = useState(user?.assigned_mbs || null);

  const effectiveAssignedMbs = liveAssignedMbs !== null && liveAssignedMbs !== undefined
    ? liveAssignedMbs
    : (user?.assigned_mbs || []);

  const displayMBs = (
    (effectiveAssignedMbs && Array.isArray(effectiveAssignedMbs) && !effectiveAssignedMbs.map(x => String(x).toLowerCase()).includes('all'))
      ? availableMBs.filter(mb => effectiveAssignedMbs.some(m => String(m).toUpperCase() === String(mb).toUpperCase()))
      : availableMBs
  );

  useEffect(() => {
    const fetchLatestUserInfo = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        if (res.data?.user) {
          if (res.data.user.settings_powers !== undefined) {
            setLivePowers(res.data.user.settings_powers);
          }
          if (res.data.user.assigned_mbs !== undefined) {
            setLiveAssignedMbs(res.data.user.assigned_mbs);
          }
          if (updateUser) {
            updateUser({
              assigned_mbs: res.data.user.assigned_mbs,
              settings_powers: res.data.user.settings_powers,
              panel_access: res.data.user.panel_access
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch user info in HandlerDashboard:', err);
      }
    };

    if (user && user.role !== 'super_admin') {
      fetchLatestUserInfo();
      const interval = setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchLatestUserInfo();
        }
      }, 30000);

      const handleFocus = () => fetchLatestUserInfo();
      window.addEventListener('focus', handleFocus);

      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [user?.id]);
  
  // Check if inquiry handler - simplified interface
  const isInquiryHandler = user?.role === 'inquiry_handler';
  // Deposit handler & calling handler get multi-tab access
  const isDepositHandler = user?.role === 'deposit_handler' || user?.role === 'calling_handler';
  
  const [chats, setChats] = useState(() => {
    try { const c = sessionStorage.getItem('hdl_chats'); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const [groups, setGroups] = useState(() => {
    try { const g = sessionStorage.getItem('hdl_groups'); return g ? JSON.parse(g) : []; } catch { return []; }
  });
  const [filteredChats, setFilteredChats] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(() => {
    try { return !sessionStorage.getItem('hdl_chats'); } catch { return true; }
  });
  const [bottomTab, setBottomTab] = useState('chats');
  const [showMenu, setShowMenu] = useState(false);
  const [mbFilter, setMbFilter] = useState('all'); // 'all' | dynamic MBs
  const [showBulkMB, setShowBulkMB] = useState(false);
  const headerMbDropdownRef = useRef(null);
  const mbDropdownRef = useRef(null);
  const [showHeaderMbMenu, setShowHeaderMbMenu] = useState(false);
  const [showMbDropdown, setShowMbDropdown] = useState(false);
  const [copyPopupChat, setCopyPopupChat] = useState(null);
  const [activeProfileCustomerId, setActiveProfileCustomerId] = useState(null);
  const [activeProfileRoomId, setActiveProfileRoomId] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(location.state?.selectedChatId || null);
  const [isDesktop, setIsDesktop] = useState(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    return !isPWA && window.innerWidth >= 1024;
  });

  // Update selectedChatId when navigating from ClientList
  useEffect(() => {
    if (location.state?.selectedChatId) {
      setSelectedChatId(location.state.selectedChatId);
    }
    if (location.state?.mbFilter) {
      setMbFilter(location.state.mbFilter);
    }
    if (location.state?.chatTypeTab) {
      setChatTypeTab(location.state.chatTypeTab);
    }
    if (location.state?.supportSubTab) {
      setSupportSubTab(location.state.supportSubTab);
    }
    if (location.state?.bottomTab) {
      setBottomTab(location.state.bottomTab);
    }
  }, [location.state]);

  // Deposit handler: chat type tab state (Deposit/Withdrawal/Support)
  const [chatTypeTab, setChatTypeTab] = useState(user?.chat_type || 'deposit');

  // Auto-close profile drawer when selected chat or tab changes
  useEffect(() => {
    setActiveProfileCustomerId(null);
    setActiveProfileRoomId(null);
  }, [selectedChatId, chatTypeTab]);

  // Detect desktop/mobile (disabled in PWA mode)
  useEffect(() => {
    const handleResize = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setIsDesktop(!isPWA && window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [unreadCounts, setUnreadCounts] = useState({}); // {room_id: count}
  
  // Selection and deletion states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showClientListModal, setShowClientListModal] = useState(false);
  const [touchStartPos, setTouchStartPos] = useState(null);
  
  const [supportSubTab, setSupportSubTab] = useState('support'); // 'support' or 'inquiry'
  // Cross-department unread counts for deposit handler tabs
  const [tabUnreadCounts, setTabUnreadCounts] = useState({ deposit: 0, withdrawal: 0, support: 0 });
  // Deposit handler: inquiry chats for settings
  const [inquiryChats, setInquiryChats] = useState([]);
  const [showInquiryInSettings, setShowInquiryInSettings] = useState(false);
  const [inquiryHasMore, setInquiryHasMore] = useState(true);
  const [inquiryLoadingMore, setInquiryLoadingMore] = useState(false);
  const [visibleChatsCount, setVisibleChatsCount] = useState(15);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
  const inquiryListRef = useRef(null);

  // Reset pagination to 15 when changing tabs, filters, or search
  useEffect(() => {
    setVisibleChatsCount(15);
  }, [activeTab, supportSubTab, chatTypeTab, mbFilter, debouncedSearchQuery]);

  useEffect(() => {
    if (user?.role !== 'deposit_handler') return;
    const fetchPtThreshold = () => {
      axios.get(`${API}/settings/pt-threshold`)
        .then((res) => setPtThreshold(Number(res.data?.minutes) || 0))
        .catch(() => {});
    };
    fetchPtThreshold();
    const interval = setInterval(fetchPtThreshold, 30000);
    return () => clearInterval(interval);
  }, [user?.role]);
  


  const activePowers = livePowers || user?.settings_powers;
  const userPowers = {
    client_list: true,
    client_directory: true,
    create_client: true,
    appearance: false,
    broadcast: true,
    status: true,
    reports: true,
    service_timelines: false,
    mobile_number_full: false,
    enquiry_handler_mb: true,
    ...(activePowers || {})
  };

  const canAccessPower = (powerKey) => {
    if (user?.role === 'super_admin') return true;
    return userPowers[powerKey] === true || userPowers[powerKey] === 'true';
  };
  
  const menuRef = useRef(null);
  const prevUnreadCountsRef = useRef({});
  const isFirstLoadRef = useRef(true);
  const notifiedRoomsRef = useRef(new Set());

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (groupsMenuRef.current && !groupsMenuRef.current.contains(e.target)) {
        setShowGroupsMenu(false);
      }
      if (gBarMenuRef.current && !gBarMenuRef.current.contains(e.target)) {
        setShowGBarMenu(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
      if (headerMbDropdownRef.current && !headerMbDropdownRef.current.contains(e.target)) {
        setShowHeaderMbMenu(false);
      }
      if (mbDropdownRef.current && !mbDropdownRef.current.contains(e.target)) {
        setShowMbDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Refresh function for manual refresh button
  const handleRefresh = async () => {
    if (!user) return;
    try {
      const chatType = isDepositHandler ? chatTypeTab : (user.chat_type || 'deposit');
      const response = await axios.get(`${API}/handler/chats`, {
        params: { handler_id: user.id, chat_type: chatType }
      });
      setChats(response.data);
      try { sessionStorage.setItem('hdl_chats', JSON.stringify(response.data)); } catch {}
      
      const groupsResponse = await axios.get(`${API}/groups/user-groups`, {
        params: { user_id: user.id }
      });
      const groupList = Array.isArray(groupsResponse.data)
        ? groupsResponse.data
        : Array.isArray(groupsResponse.data?.groups)
        ? groupsResponse.data.groups
        : [];
      setGroups(groupList);
      try { sessionStorage.setItem('hdl_groups', JSON.stringify(groupList)); } catch {}
      
      toast.success('Refreshed!');
    } catch (error) {
      console.error('Refresh failed:', error);
      toast.error('Refresh failed');
    }
  };
  
  // Load inquiry chats for deposit handler settings
  const loadInquiryChats = async () => {
    if (!user || !isDepositHandler || user?.role === 'calling_handler') return;
    try {
      const response = await axios.get(`${API}/handler/chats`, {
        params: { handler_id: user.id, chat_type: 'inquiry' }
      });
      setInquiryChats(response.data || []);
    } catch (error) {
      console.error('Failed to load inquiry chats:', error);
    }
  };

  // Handle navigation state from CreateClient back button
  useEffect(() => {
    if (location.state?.bottomTab) {
      setBottomTab(location.state.bottomTab);
    }
    // Restore chat type tab for deposit handler
    if (location.state?.chatTypeTab && isDepositHandler) {
      setChatTypeTab(location.state.chatTypeTab);
    }
    // Clear the state to prevent it from persisting
    window.history.replaceState({}, document.title);
  }, [location]);
  
  // ========== ONLINE STATUS HEARTBEAT ==========
  // Mark handler as online when on dashboard (critical for delivery status)
  useEffect(() => {
    if (!user?.id) return;
    
    // Connect socket for real-time calls & notifications
    connectSocket(user.id);
    
    // Set online immediately when dashboard loads
    axios.post(`${API}/users/online-status?user_id=${user.id}`, {
      is_online: true
    }).catch(err => console.error('❌ Online status update failed:', err));
    
    // Heartbeat every 30s — sufficient to maintain online presence
    const heartbeatInterval = setInterval(() => {
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      }).catch(err => console.error('❌ Heartbeat failed:', err));
    }, 30000);
    
    // Set offline when leaving dashboard
    return () => {
      clearInterval(heartbeatInterval);
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: false
      }).catch(err => console.error('❌ Offline status update failed:', err));
    };
  }, [user?.id]);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await axios.get(`${API}/chat/header-badges`);
        setBadges(res.data);
      } catch (err) {
        console.error('Failed to fetch header badges:', err);
      }
    };
    
    fetchBadges();
    const interval = setInterval(fetchBadges, 8000);
    window.addEventListener('refresh-header-badges', fetchBadges);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('refresh-header-badges', fetchBadges);
    };
  }, []);

  useEffect(() => {
    const clickOutside = (e) => {
      if (groupsMenuRef.current && !groupsMenuRef.current.contains(e.target)) {
        setShowGroupsMenu(false);
      }
      if (gBarMenuRef.current && !gBarMenuRef.current.contains(e.target)) {
        setShowGBarMenu(false);
      }
      if (nMenuRef.current && !nMenuRef.current.contains(e.target)) {
        setShowNMenu(false);
      }
      if (ptMenuRef.current && !ptMenuRef.current.contains(e.target)) {
        setShowPtMenu(false);
        setShowPtThresholdMenu(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', clickOutside);
    return () => document.removeEventListener('mousedown', clickOutside);
  }, []);

  const handleToggleNMenu = async () => {
    if (!showNMenu) {
      setShowNMenu(true);
      setShowPtMenu(false);
      setShowGroupsMenu(false);
      setLoadingNList(true);
      try {
        const res = await axios.get(`${API}/chat/badge-details/n`);
        setNList(res.data || []);
      } catch (err) {
        console.error('Failed to load unassigned clients:', err);
      } finally {
        setLoadingNList(false);
      }
    } else {
      setShowNMenu(false);
    }
  };

  const handleTogglePtMenu = async () => {
    if (!showPtMenu) {
      setShowPtMenu(true);
      setShowNMenu(false);
      setShowGroupsMenu(false);
      setLoadingPtList(true);
      try {
        const res = await axios.get(`${API}/chat/badge-details/pt`);
        setPtList(res.data || []);
      } catch (err) {
        console.error('Failed to load pending tasks:', err);
      } finally {
        setLoadingPtList(false);
      }
    } else {
      setShowPtMenu(false);
      setShowPtThresholdMenu(false);
    }
  };

  // Cross-department notifications via Socket.IO
  const notifiedMsgIds = useRef(new Set());
  useEffect(() => {
    if (!user?.id || !isDepositHandler) return;
    
    const handleNewMessage = async (data) => {
      if (data.message && data.message.sender_id !== user.id && data.room_id) {
        // Dedup: don't notify for same message twice
        if (notifiedMsgIds.current.has(data.message.id)) return;
        notifiedMsgIds.current.add(data.message.id);
        // Limit set size
        if (notifiedMsgIds.current.size > 200) {
          const arr = [...notifiedMsgIds.current];
          notifiedMsgIds.current = new Set(arr.slice(-100));
        }
        
        try {
          const roomRes = await axios.get(`${API}/chat/room/${data.room_id}`);
          const chatType = roomRes.data?.chat_type;
          if (chatType === (user.chat_type || 'deposit')) {
            showNotification({
              title: `New message - ${data.message.sender_name || 'Customer'}`,
              body: data.message.text || 'New message',
              tag: `handler-${data.room_id}`
            });
          }
          setTabUnreadCounts(prev => ({
            ...prev,
            [chatType]: (prev[chatType] || 0) + 1
          }));
        } catch (e) {}
      }
    };
    
    onNewMessage(handleNewMessage);
    return () => offNewMessage(handleNewMessage);
  }, [user?.id, isDepositHandler]);

  // Real-time synchronization when a customer is registered
  useEffect(() => {
    const handleCustomerRegistered = (data) => {
      if (!data) return;
      setChats(prevChats => prevChats.map(c => {
        if (c.id === data.roomId || c.room_id === data.roomId || (data.customerId && (c.customer_id === data.customerId || c.customer?.id === data.customerId))) {
          return {
            ...c,
            customer_name: data.name || c.customer_name,
            customer_phone: data.phone || c.customer_phone,
            customer_is_guest: false,
            is_guest: false
          };
        }
        return c;
      }));
    };
    onCustomerRegistered(handleCustomerRegistered);
    return () => offCustomerRegistered(handleCustomerRegistered);
  }, []);

  // Fetch cross-department unread counts for tab badges
  useEffect(() => {
    if (!user?.id || !isDepositHandler) return;
    
    const fetchTabCounts = async () => {
      try {
        const counts = { deposit: 0, withdrawal: 0, support: 0 };
        for (const type of ['deposit', 'withdrawal', 'support']) {
          const res = await axios.get(`${API}/chat/unread-count`, {
            params: { user_id: user.id, chat_type: type }
          });
          counts[type] = res.data.unread_count ?? (res.data.unread_rooms || []).length;
        }
        setTabUnreadCounts(counts);
      } catch (e) {}
    };
    
    fetchTabCounts();
    const stopPoll = startPolling('handler-tab-counts', fetchTabCounts, 5000);
    return () => stopPoll();
  }, [user?.id, isDepositHandler]);

  useEffect(() => {
    const handleMarkedRead = (e) => {
      if (e.detail?.roomId) {
        setUnreadCounts(prev => ({
          ...prev,
          [e.detail.roomId]: 0
        }));
        setChats(prev => prev.map(c => (c.room_id === e.detail.roomId || c.id === e.detail.roomId) ? { ...c, unread_count: 0 } : c));
      }
    };
    window.addEventListener('messages-marked-read', handleMarkedRead);
    return () => window.removeEventListener('messages-marked-read', handleMarkedRead);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Load chats
  useEffect(() => {
    if (!user) return;

    const loadChats = async () => {
      try {
        let chatType = isDepositHandler ? chatTypeTab : (user.chat_type || 'deposit');
        // When on support tab, use supportSubTab to filter (except calling_handler who only sees support)
        if (user?.role === 'calling_handler' && chatTypeTab === 'support') {
          chatType = 'support';
        } else if ((isDepositHandler && chatTypeTab === 'support') || user?.chat_type === 'support' || user?.role === 'support_handler') {
          chatType = supportSubTab; // 'support' or 'inquiry'
        }
        const response = await axios.get(`${API}/handler/chats`, {
          params: {
            handler_id: user.id,
            chat_type: chatType
          }
        });
        const responseChats = Array.isArray(response.data) ? response.data : (response.data?.rooms || []);
        
        // Only update state if data actually changed (prevents unnecessary re-renders)
        setChats(prev => {
          const newData = responseChats;
          if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
          try { sessionStorage.setItem('hdl_chats', JSON.stringify(newData)); } catch {}
          return newData;
        });
        
        // Also load groups
        const groupsResponse = await axios.get(`${API}/groups/user-groups`, {
          params: { user_id: user.id }
        });
        setGroups(prev => {
          const newData = Array.isArray(groupsResponse.data)
            ? groupsResponse.data
            : Array.isArray(groupsResponse.data?.groups)
            ? groupsResponse.data.groups
            : [];
          if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
          try { sessionStorage.setItem('hdl_groups', JSON.stringify(newData)); } catch {}
          return newData;
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to load chats:', error);
        setLoading(false);
      }
    };

    const loadUnreadCounts = async () => {
      try {
        const response = await axios.get(`${API}/chat/unread-count`, {
          params: {
            user_id: user.id,
            ...(((isDepositHandler && chatTypeTab === 'support') || user?.chat_type === 'support' || user?.role === 'support_handler') && supportSubTab === 'inquiry'
              ? { chat_type: 'inquiry' }
              : {})
          }
        });
        
        // Convert array to object: {room_id: count}
        const countsMap = {};
        if (response.data.unread_rooms) {
          response.data.unread_rooms.forEach(item => {
            countsMap[item.room_id] = item.count;
          });
        }

        // Automatically zero out unread count for the room currently open on screen
        if (selectedChatId) {
          countsMap[selectedChatId] = 0;
        }
        
        // ========== NOTIFICATION LOGIC ==========
        // Skip first load to prevent notification replay on navigation back
        if (isFirstLoadRef.current) {
          isFirstLoadRef.current = false;
        } else {
          const prevCounts = prevUnreadCountsRef.current;
          
          Object.keys(countsMap).forEach(roomId => {
            const currentCount = countsMap[roomId];
            const prevCount = prevCounts[roomId] || 0;
            
            // New unread message detected - but not if we're actively viewing that chat
            if (currentCount > prevCount && !notifiedRoomsRef.current.has(roomId) && roomId !== selectedChatId) {
              // Find chat details for better notification
              const chat = chats.find(c => c.id === roomId);
              const customerName = chat?.customer_name || chat?.customer?.username || 'Customer';
              const chatType = chat?.chat_type || 'chat';
              
              // Show notification with sound
              showNotification({
                title: `${customerName} - ${chatType.toUpperCase()}`,
                body: 'New message received',
                tag: `handler-dashboard-${roomId}`,
                data: { roomId, chatType }
              });
              
              // Mark this room as notified
              notifiedRoomsRef.current.add(roomId);
              
              // Remove from notified set after 5 seconds so subsequent messages can trigger again
              setTimeout(() => {
                notifiedRoomsRef.current.delete(roomId);
              }, 5000);
            }
          });
        }
        
        // Update prev counts ref
        prevUnreadCountsRef.current = countsMap;
        
        // Smart setState - only re-render if counts actually changed
        setUnreadCounts(prev => {
          if (JSON.stringify(prev) === JSON.stringify(countsMap)) return prev;
          // Update PWA badge with total unread count
          const totalUnread = Object.values(countsMap).reduce((sum, c) => sum + c, 0);
          updateAppBadge(totalUnread);
          return countsMap;
        });
      } catch (error) {
        console.error('Failed to load unread counts:', error);
      }
    };

    loadChats();
    loadUnreadCounts();
    
    // Background-safe polling using Web Worker
    const stopPoll = startPolling('handler-dashboard', () => {
      loadChats();
      loadUnreadCounts();
    }, 3000);
    const cleanupVis = onVisibilityFetch(() => {
      loadChats();
      loadUnreadCounts();
    });
    return () => { stopPoll(); cleanupVis(); };
  }, [user, chatTypeTab, supportSubTab]);

  const loadMoreInquiryChats = async () => {
    if (inquiryLoadingMore || !inquiryHasMore || !user) return;
    const chatType = (isDepositHandler && chatTypeTab === 'support') || user?.chat_type === 'support' || user?.role === 'support_handler'
      ? supportSubTab
      : (isDepositHandler ? chatTypeTab : user.chat_type || 'deposit');
    if (chatType !== 'inquiry') return;
    setInquiryLoadingMore(true);
    try {
      const response = await axios.get(`${API}/handler/chats`, {
        params: { handler_id: user.id, chat_type: 'inquiry', limit: 15, offset: chats.length }
      });
      const next = Array.isArray(response.data) ? response.data : (response.data?.rooms || []);
      setChats(prev => {
        const seen = new Set(prev.map(chat => chat.room_id || chat.id));
        return [...prev, ...next.filter(chat => !seen.has(chat.room_id || chat.id))];
      });
      setInquiryHasMore(next.length >= 15);
    } catch (error) {
      console.error('Failed to load more chats:', error);
    } finally {
      setInquiryLoadingMore(false);
    }
  };

  const handleInquiryListScroll = (event) => {
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 120) {
      if (visibleChatsCount < filteredChats.length && !isLoadingMoreChats) {
        setIsLoadingMoreChats(true);
        setTimeout(() => {
          setVisibleChatsCount(prev => Math.min(prev + 15, filteredChats.length));
          setIsLoadingMoreChats(false);
        }, 150);
      }
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter chats based on search and active tab
  useEffect(() => {
    let filtered = [...chats];

    // If viewing archive tab, only show archived chats
    if (activeTab === 'archive') {
      filtered = filtered.filter(chat => chat.is_archived === true);
      if (debouncedSearchQuery) {
        filtered = filtered.filter(chat =>
          chat.customer.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
          chat.customer.phone.includes(debouncedSearchQuery)
        );
      }
    } else {
      // Exclude archived chats from all other active views
      if (activeTab !== 'groups') {
        filtered = filtered.filter(chat => !chat.is_archived);
      }

      // Search filter (using debounced query)
      if (debouncedSearchQuery) {
        if (activeTab === 'groups') {
          filtered = groups.filter(group =>
            !group.name?.toLowerCase().includes('withdraw group') &&
            group.name?.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
          );
        } else {
          filtered = filtered.filter(chat =>
            chat.customer.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
            chat.customer.phone.includes(debouncedSearchQuery)
          );
          // Apply tab filter after search
          if (activeTab === 'unread') {
            filtered = filtered.filter(chat => (unreadCounts[chat.room_id || chat.id] ?? chat.unread_count ?? 0) > 0);
          } else if (activeTab === 'favourites') {
            filtered = filtered.filter(chat => chat.is_favourite === true);
          }
        }
      } else {
        // No search - apply tab filter only
        if (activeTab === 'unread') {
          filtered = filtered.filter(chat => (unreadCounts[chat.room_id || chat.id] ?? chat.unread_count ?? 0) > 0);
        } else if (activeTab === 'favourites') {
          filtered = chats.filter(chat => chat.is_favourite === true && !chat.is_archived);
        } else if (activeTab === 'groups') {
          filtered = groups.filter(group => !group.name?.toLowerCase().includes('withdraw group'));
        }
      }
    }

    // Support sub-tab filter for Handler
    if ((chatTypeTab === 'support' || user?.chat_type === 'support' || user?.role === 'support_handler') && activeTab !== 'groups' && activeTab !== 'archive') {
      if (supportSubTab === 'support') {
        filtered = filtered.filter(chat => {
          const mb = chat.mb_setup || chat.customer?.mb_setup;
          const hasAssignedMB = Boolean(mb && mb !== 'N' && mb !== 'Unassigned');
          return hasAssignedMB && (chat.chat_type === 'support' || chat.chat_type === 'inquiry');
        });
      } else if (supportSubTab === 'inquiry') {
        // Show all inquiry chats on Inquiry Support tab (no MB filter)
        filtered = filtered.filter(chat => chat.chat_type === 'inquiry');
      }
    }

    // Deposit & Withdrawal views - require assigned MB
    const isDepositOrWithdrawal = !isInquiryHandler && (
      ['deposit', 'withdrawal'].includes(user?.chat_type || '') ||
      (isDepositHandler && ['deposit', 'withdrawal'].includes(chatTypeTab))
    );
    if (isDepositOrWithdrawal && activeTab !== 'groups' && activeTab !== 'archive') {
      filtered = filtered.filter(chat => {
        const mb = chat.mb_setup || chat.customer?.mb_setup;
        return Boolean(mb && mb !== 'N' && mb !== 'Unassigned');
      });
    }

    // MB Setup filter — applies in deposit / withdrawal / support views (disabled for Inquiry subtab)
    const showMBTabsFilter = !isInquiryHandler && (
      ['deposit', 'withdrawal', 'support'].includes(user?.chat_type || '') ||
      (isDepositHandler && ['deposit', 'withdrawal', 'support'].includes(chatTypeTab))
    );
    if (showMBTabsFilter && activeTab !== 'groups' && mbFilter !== 'all' && supportSubTab !== 'inquiry') {
      filtered = filtered.filter(chat => (chat.mb_setup || chat.customer?.mb_setup) === mbFilter);
    }

    // Deduplicate chats by customer_id to prevent duplicate chat cards
    const seenCustomers = new Set();
    filtered = filtered.filter(chat => {
      if (chat.is_group) return true;
      const key = chat.customer_id || chat.customer?.id || chat.id;
      if (seenCustomers.has(key)) return false;
      seenCustomers.add(key);
      return true;
    });

    if (supportSubTab === 'inquiry' || chatTypeTab === 'inquiry' || user?.chat_type === 'inquiry') {
      filtered.sort((a, b) => {
        // Keep inquiry ordering based on latest activity, not unread count.
        // Reading a chat changes its badge only; a new message moves it to the top.
        const aTime = new Date(a.last_message?.timestamp || a.last_message?.created_at || a.last_message_at || a.updated_at || a.created_at || 0).getTime();
        const bTime = new Date(b.last_message?.timestamp || b.last_message?.created_at || b.last_message_at || b.updated_at || b.created_at || 0).getTime();
        return bTime - aTime;
      });
    }

    setFilteredChats(filtered);
  }, [chats, groups, unreadCounts, debouncedSearchQuery, activeTab, mbFilter, chatTypeTab, supportSubTab, isDepositHandler, isInquiryHandler, user?.chat_type]);

  const handleChatClick = (chat) => {
    if (isDesktop) {
      setSelectedChatId(chat.room_id);
    } else {
      navigate(`/handler-chat/${chat.room_id}`, { state: { chatTypeTab, supportSubTab, mbFilter } });
    }
  };

  const handleGroupClick = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  const toggleFavourite = async (roomId, currentStatus) => {
    try {
      await axios.post(`${API}/chat/rooms/${roomId}/favourite`, {
        user_id: user.id
      });
      
      // Update local state
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.room_id === roomId 
            ? { ...chat, is_favourite: !currentStatus }
            : chat
        )
      );
      
      toast.success(currentStatus ? 'Removed from favourites' : 'Added to favourites');
    } catch (error) {
      console.error('Toggle favourite error:', error);
      toast.error('Failed to update favourite');
    }
  };

  // Copy client bank details
  const copyClientDetails = async (chat) => {
    try {
      // Fetch client full details
      const response = await axios.get(`${API}/clients/registered`);
      const customerId = chat.customer_id || chat.customer?.id;
      const client = response.data.find(c => 
        c.id === customerId || 
        c.phone === chat.customer_phone ||
        c.phone === chat.customer?.phone
      );
      
      if (!client) {
        toast.error('Client details not found');
        return;
      }
      
      // Format: Username, A/c Holder, Bank, IFSC, Bank Name, Address, UPI
      let formattedText = `User Name: ${client.username || 'N/A'}

A/c Holder Name: ${client.account_holder_name || client.client_name || client.username || 'N/A'}

Bank Account No: ${client.bank_account_number || 'N/A'}

IFSC Code: ${client.ifsc_code || 'N/A'}

Bank Name: ${client.bank_name || 'N/A'}`;

      if (client.address) {
        formattedText += `\n\nAddress: ${client.address}`;
      }
      if (client.upi_id) {
        formattedText += `\n\nUPI ID: ${client.upi_id}`;
      }

      // Fetch dynamic customer login link from settings
      try {
        const settingsRes = await axios.get(`${API}/settings`);
        const loginLink = settingsRes.data?.customer_login_link;
        if (loginLink) {
          formattedText += `\nLogin Link: ${loginLink}`;
        }
      } catch (e) { /* skip if settings fetch fails */ }

      await navigator.clipboard.writeText(formattedText);
      toast.success('Details copied with login link!');
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy details');
    }
  };

  // Open WhatsApp with client number (PWA-compatible)
  const openWhatsApp = async (phone, roomId = null, customerId = null) => {
    if (roomId || customerId) {
      try {
        await axios.post(`${API}/calling-reports/activity`, {
          room_id: roomId,
          customer_id: customerId,
          kind: 'whatsapp'
        });
        toast.success('WhatsApp redirect logged in chat');
      } catch (e) { console.error('Error logging WhatsApp activity:', e); }
    }
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    // Remove any non-numeric characters and add country code if needed
    const cleanNumber = phone.replace(/\D/g, '');
    const whatsappNumber = cleanNumber.startsWith('91') ? cleanNumber : `91${cleanNumber}`;
    // Use anchor click for PWA compatibility
    const link = document.createElement('a');
    link.href = `https://wa.me/${whatsappNumber}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogout = () => {
    logout(); // logout() handles redirect automatically
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  // Long-press handlers for selection mode
  const handleLongPressStart = (roomId, e) => {
    e.preventDefault();
    
    // Get touch/mouse position
    const pos = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
    setTouchStartPos(pos);
    
    const timer = setTimeout(() => {
      setSelectionMode(true);
      setSelectedRooms([roomId]);
    }, 1000); // 1 second long press (WhatsApp style)
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setTouchStartPos(null);
  };

  const handleTouchMove = (e) => {
    if (!touchStartPos || !longPressTimer) return;
    
    // Get current position
    const currentPos = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
    
    // Calculate distance moved
    const distance = Math.sqrt(
      Math.pow(currentPos.x - touchStartPos.x, 2) + 
      Math.pow(currentPos.y - touchStartPos.y, 2)
    );
    
    // Only cancel if moved more than 10px (allows for slight movement)
    if (distance > 10) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
      setTouchStartPos(null);
    }
  };

  const toggleRoomSelection = (roomId) => {
    if (selectedRooms.includes(roomId)) {
      setSelectedRooms(prev => prev.filter(id => id !== roomId));
    } else {
      setSelectedRooms(prev => [...prev, roomId]);
    }
  };

  const handleDeleteRooms = async () => {
    try {
      if (selectedRooms.length === 1) {
        await axios.delete(`${API}/chat/room/${selectedRooms[0]}?user_id=${user.id}`);
      } else {
        await axios.post(`${API}/chat/rooms/bulk-delete`, {
          room_ids: selectedRooms,
          user_id: user.id
        });
      }
      
      // Reload chats
      setChats(prev => prev.filter(c => !selectedRooms.includes(c.room_id)));
      setFilteredChats(prev => prev.filter(c => !selectedRooms.includes(c.room_id)));
      
      // Reset selection mode
      setSelectionMode(false);
      setSelectedRooms([]);
      setShowDeleteConfirm(false);
      
    } catch (error) {
      console.error('Failed to delete rooms:', error);
      toast.error('Failed to delete chats. Please try again.');
    }
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelectedRooms([]);
  };

  const handleToggleArchive = async () => {
    try {
      const isUnarchiving = activeTab === 'archive';
      
      for (const roomId of selectedRooms) {
        await axios.post(`${API}/chat/rooms/${roomId}/archive`, null, {
          params: { user_id: user.id, unarchive: isUnarchiving }
        });
      }
      
      toast.success(isUnarchiving ? 'Chats unarchived' : 'Chats archived');
      
      // Reload rooms to reflect changes
      setSelectionMode(false);
      setSelectedRooms([]);
      await handleRefresh();
    } catch (error) {
      console.error('Failed to toggle archive:', error);
      toast.error('Failed to archive chats');
    }
  };

  const handleAddToClientList = async () => {
    try {
      let successCount = 0;
      let errorCount = 0;
      let alreadyExistsCount = 0;
      
      for (const roomId of selectedRooms) {
        try {
          const response = await axios.post(`${API}/chat/room/add-to-client-list`, {
            room_id: roomId,
            user_id: user.id
          });
          
          if (response.data.message === "Already in client list") {
            alreadyExistsCount++;
          } else {
            successCount++;
          }
        } catch (err) {
          console.error(`Failed to add room ${roomId}:`, err);
          errorCount++;
        }
      }
      
      // Show appropriate toast messages
      if (successCount > 0) {
        toast.success(`${successCount} chat${successCount > 1 ? 's' : ''} added to client list!`);
      }
      
      if (alreadyExistsCount > 0) {
        toast.info(`${alreadyExistsCount} chat${alreadyExistsCount > 1 ? 's are' : ' is'} already in client list`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} chat${errorCount > 1 ? 's' : ''}`);
      }
      
      setShowClientListModal(false);
      setSelectionMode(false);
      setSelectedRooms([]);
      
    } catch (error) {
      console.error('Failed to add to client list:', error);
      toast.error('Failed to add to client list. Please try again.');
    }
  };

  const unreadCount = chats.filter(c => {
    const count = unreadCounts[c.room_id || c.id] ?? c.unread_count ?? 0;
    return count > 0;
  }).length;

  // Mini component to show bank preview in copy popup
  const CopyPopupBankPreview = ({ customerId, phone }) => {
    const [bankData, setBankData] = React.useState(null);
    React.useEffect(() => {
      axios.get(`${API}/clients/registered`).then(res => {
        const client = res.data.find(c => c.id === customerId || c.phone === phone);
        if (client) setBankData(client);
      }).catch(() => {});
    }, [customerId, phone]);
    if (!bankData) return <p className="text-xs text-wa-text-secondary">Loading bank details...</p>;
    if (!bankData.bank_name && !bankData.bank_account_number) return <p className="text-xs text-wa-text-secondary">No bank details saved</p>;
    return (
      <div className="text-xs text-wa-text-secondary space-y-0.5">
        {bankData.bank_name && <p>Bank: {bankData.bank_name}</p>}
        {bankData.account_holder_name && <p>Holder: {bankData.account_holder_name}</p>}
        {bankData.bank_account_number && <p>A/c: {bankData.bank_account_number}</p>}
        {bankData.ifsc_code && <p>IFSC: {bankData.ifsc_code}</p>}
        {bankData.upi_id && <p>UPI: {bankData.upi_id}</p>}
      </div>
    );
  };

  const [maintenance, setMaintenance] = useState(null);

  // Check Maintenance Mode
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await axios.get(`${API}/settings/maintenance`);
        setMaintenance(res.data || null);
      } catch (e) {}
    };
    checkMaintenance();
    const interval = setInterval(checkMaintenance, 10000);
    return () => clearInterval(interval);
  }, []);

  if (maintenance?.enabled && ['handlers', 'both'].includes(maintenance?.who_blocked)) {
    return (
      <div className="fixed inset-0 bg-slate-900 z-[999] flex flex-col items-center justify-center p-6 text-center text-white font-sans animate-fade-in">
        <div className="w-16 h-16 bg-amber-500/20 text-amber-400 rounded-full flex items-center justify-center mb-4 border border-amber-500/30 shadow-lg animate-pulse">
          <Wrench size={32} weight="bold" />
        </div>
        <h2 className="text-xl font-bold mb-2">Maintenance Mode</h2>
        <p className="text-sm text-slate-300 max-w-sm leading-relaxed mb-6">
          {maintenance.message || "We're doing some quick maintenance. Please check back shortly."}
        </p>
        <div className="text-[11px] text-slate-500 font-medium">
          DWS Vault Support System
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-wa-panel overflow-hidden">
      {/* Full Width Top Header */}
      <div className="bg-[#008069] text-white p-2.5 sm:p-3 flex-shrink-0 relative z-[600] w-full">
        <div className="flex items-center justify-between gap-1 sm:gap-2">
          <div className="cursor-pointer flex items-center gap-1.5 flex-shrink-0" onClick={() => { setSelectedChatId(null); setBottomTab('chats'); }}>
            <img src="/icons/icon-128x128.png" alt="DWS" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full" />
            <div className="hidden sm:block">
              <h1 className="font-bold leading-tight text-xs sm:text-sm">DWS Support</h1>
              <p className="text-[9px] sm:text-[10px] text-white/70">
                {isDepositHandler 
                  ? `${chatTypeTab.charAt(0).toUpperCase() + chatTypeTab.slice(1)} Panel`
                  : user?.chat_type 
                    ? `${user.chat_type.charAt(0).toUpperCase() + user.chat_type.slice(1)} Panel` 
                    : 'Handler Panel'}
              </p>
            </div>
          </div>

          {/* Header Center: All MBs Oval Pill Dropdown */}
          <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-1">
            <div className="relative" ref={headerMbDropdownRef}>
              <button
                onClick={() => setShowHeaderMbMenu(!showHeaderMbMenu)}
                className="px-3 py-0.5 sm:py-1 rounded-full border-2 border-emerald-300 bg-white/10 hover:bg-white/20 text-white font-black text-xs flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
              >
                <span>{mbFilter === 'all' ? 'All MBs' : mbFilter}</span>
                <CaretDown size={12} weight="bold" className={`transition-transform duration-200 ${showHeaderMbMenu ? 'rotate-180' : ''}`} />
              </button>

              {showHeaderMbMenu && (
                <div className="absolute top-8 left-1/2 -translate-x-1/2 bg-white dark:bg-[#111b21] border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[999] w-48 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                  <button
                    onClick={() => { setMbFilter('all'); setShowHeaderMbMenu(false); }}
                    className={`w-full px-3.5 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between border-b border-gray-100 dark:border-gray-800 ${mbFilter === 'all' ? 'font-black text-emerald-600' : ''}`}
                  >
                    <span>All MBs</span>
                    <span className="bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-full font-bold text-[10px]">
                      {chats.filter(c => {
                        const mb = c.mb_setup || c.customer?.mb_setup;
                        return mb && mb !== 'N' && mb !== 'Unassigned';
                      }).length}
                    </span>
                  </button>
                  <div className="max-h-56 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/40">
                    {displayMBs.map((mb) => {
                      const count = chats.filter(c => (c.mb_setup || c.customer?.mb_setup) === mb).length;
                      return (
                        <button
                          key={mb}
                          onClick={() => { setMbFilter(mb); setShowHeaderMbMenu(false); }}
                          className={`w-full px-3.5 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between transition-colors ${mbFilter === mb ? 'bg-emerald-50 dark:bg-emerald-950/60 font-bold text-emerald-600' : ''}`}
                        >
                          <span className="truncate">{mb}</span>
                          <span className="text-[10px] font-semibold text-slate-400">({count})</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <span className="text-[9px] sm:text-[10px] font-bold text-emerald-100 bg-emerald-700/80 px-2 py-0.2 rounded-full mt-0.5 capitalize shadow-2xs">
              {isDepositHandler 
                ? (chatTypeTab === 'support' ? `${supportSubTab} panel` : `${chatTypeTab} panel`)
                : user?.chat_type ? `${user.chat_type} panel` : 'handler panel'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Header Badges: PW, N, G, PT - restricted to super_admin or withdrawal handlers */}
            {(user?.role === 'super_admin' || user?.role === 'withdrawal_handler' || (user?.role === 'handler' && user?.chat_type === 'withdrawal') || isDepositHandler || user?.role === 'support_handler') && (
              <div className="flex items-center gap-1 sm:gap-1.5 mr-0.5 sm:mr-1.5">
                 {/* S Badge (Support Normal) */}
                 {(user?.role === 'super_admin' || isDepositHandler || user?.role === 'support_handler') && (
                   <button
                     onClick={() => { setChatTypeTab('support'); setSupportSubTab('support'); setMbFilter('all'); }}
                     className={`relative w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer ${
                       chatTypeTab === 'support' && supportSubTab === 'support'
                         ? 'bg-teal-600 ring-2 ring-white text-white scale-105'
                         : 'bg-teal-500 hover:bg-teal-600 text-white'
                     }`}
                     title="S: Support Normal Panel"
                   >
                     <span>S</span>
                   </button>
                 )}

                 {/* I Badge (Inquiry Support) */}
                  {user?.role !== 'calling_handler' && (user?.role === 'super_admin' || isDepositHandler || user?.role === 'support_handler') && (
                   <button
                     onClick={() => { setActiveSubSetting(null); setActiveTab('all'); setChatTypeTab('support'); setSupportSubTab('inquiry'); setMbFilter('all'); }}
                     className={`relative w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer ${
                       chatTypeTab === 'support' && supportSubTab === 'inquiry'
                         ? 'bg-purple-700 ring-2 ring-white text-white scale-105'
                         : 'bg-purple-600 hover:bg-purple-700 text-white'
                     }`}
                     title="I: Inquiry Support Panel"
                   >
                     <span>I</span>
                   </button>
                 )}

                 {/* PW Badge */}
                 {(user?.role === 'super_admin' || user?.role === 'deposit_handler') && (
                   <button 
                     onClick={() => setActiveBadgeView('pw')}
                     className="relative w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none"
                     title="PW: Pending Withdrawals (>30 min)"
                   >
                     <span>PW</span>
                     {badges.pw > 0 && (
                       <span className="absolute -bottom-1 -right-1 bg-red-600 text-white rounded-full text-[9px] font-black w-4 h-4 flex items-center justify-center border border-white shadow-sm animate-pulse">
                         {badges.pw}
                       </span>
                     )}
                   </button>
                 )}

                 {/* N Badge (Unassigned Members Dropdown Toggle) */}
                 {(user?.role === 'super_admin' || user?.role === 'deposit_handler') && (
                   <div className="relative" ref={nMenuRef}>
                     <button 
                       onClick={handleToggleNMenu}
                       className="relative w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer"
                       title="N: Unassigned Members (No MB Yet)"
                     >
                       <span>N</span>
                       {badges.n > 0 && (
                         <span className="absolute -bottom-1 -right-1 bg-red-600 text-white rounded-full text-[9px] font-black w-4 h-4 flex items-center justify-center border border-white shadow-sm">
                           {badges.n}
                         </span>
                       )}
                     </button>

                       {showNMenu && (
                         <div className="absolute right-[-144px] top-10 bg-white dark:bg-[#111b21] border border-gray-200 dark:border-gray-700/80 text-wa-text-primary rounded-2xl shadow-2xl z-[999] w-[310px] sm:w-[340px] overflow-hidden text-xs animate-in fade-in zoom-in-95 duration-100">
                           {/* Header matching user mockup */}
                           <div className="px-3.5 py-2.5 bg-slate-100/90 dark:bg-[#1f2c34] border-b border-gray-200 dark:border-gray-700/60 flex items-center justify-between">
                             <div className="flex items-center gap-1.5 min-w-0">
                               <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                               <span className="text-[11px] font-black uppercase tracking-wider text-slate-800 dark:text-slate-100 truncate">
                                 NEW CLIENT — NO MB YET
                               </span>
                             </div>
                             <button
                               onClick={() => {
                                 setShowNMenu(false);
                                 setActiveBadgeView('n');
                               }}
                               className="text-[11px] font-extrabold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-100 dark:hover:bg-blue-900/70 px-2.5 py-1 rounded-full border border-blue-200 dark:border-blue-800/60 transition-all flex items-center gap-1 cursor-pointer flex-shrink-0 shadow-2xs"
                             >
                               View all <span className="font-black text-blue-700 dark:text-blue-300">({badges.n || nList.length})</span>
                             </button>
                           </div>
                          {/* List */}
                          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800/60">
                            {loadingNList ? (
                              <div className="p-4 text-center text-gray-400 text-xs">Loading unassigned clients...</div>
                            ) : nList.length === 0 ? (
                              <div className="p-4 text-center text-gray-400 text-xs italic">No unassigned clients found.</div>
                            ) : (
                              nList.map((item, idx) => (
                                <div key={item.customer_id || idx} className="p-2.5 hover:bg-slate-50 dark:hover:bg-[#182229] flex items-center justify-between gap-2 transition-colors">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950/80 dark:text-blue-300 font-extrabold text-[11px] flex items-center justify-center border border-blue-200 dark:border-blue-800/60 flex-shrink-0">
                                      {idx + 1}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                        {item.customer_name}
                                      </p>
                                      <p className="text-[10px] text-slate-400 dark:text-slate-400 truncate">
                                        {item.phone !== 'N/A' ? item.phone : (item.is_new_today ? 'New today' : 'Unassigned')}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {/* Assign MB Selector */}
                                    <select
                                      key={`n-select-handler-${item.customer_id}`}
                                      value=""
                                      onChange={async (e) => {
                                        const selectedMB = e.target.value;
                                        if (!selectedMB) return;
                                        try {
                                          await axios.put(`${API}/clients/${item.customer_id}/mb-setup`, { mb_setup: selectedMB });
                                          toast.success(`Assigned ${item.customer_name} to ${selectedMB}`);
                                          // Optimistically remove from list & decrement badge count
                                          setNList(prev => prev.filter(c => c.customer_id !== item.customer_id));
                                          setBadges(prev => ({ ...prev, n: Math.max(0, (prev.n || 1) - 1) }));
                                        } catch (err) {
                                          toast.error('Failed to assign MB');
                                        }
                                      }}
                                      className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-900 dark:bg-amber-950/70 dark:text-amber-200 text-[10px] font-bold rounded-lg border border-amber-300/80 dark:border-amber-700/70 cursor-pointer outline-none transition-colors shadow-2xs"
                                    >
                                      <option value="" disabled>🏷️ Assign MB ▾</option>
                                      {availableMBs.map(mb => (
                                        <option key={mb} value={mb}>{mb}</option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                   </div>
                 )}

                  {/* G Badge (Groups Dropdown Toggle) */}
                  {user?.role !== 'calling_handler' && (user?.role === 'super_admin' || user?.role === 'admin' || isDepositHandler || user?.role === 'deposit_handler' || user?.role === 'withdrawal_handler' || user?.role === 'support_handler' || (user?.role === 'handler' && (user?.chat_type === 'deposit' || user?.chat_type === 'withdrawal'))) && (
                    <div className="relative" ref={groupsMenuRef}>
                   <button 
                     onClick={() => setShowGroupsMenu(!showGroupsMenu)}
                     className="relative w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none"
                     title="G: Groups (Pending requests)"
                   >
                     <span>G</span>
                     {badges.g > 0 && (
                       <span className="absolute -bottom-1 -right-1 bg-red-600 text-white rounded-full text-[9px] font-black w-4 h-4 flex items-center justify-center border border-white shadow-sm">
                         {badges.g}
                       </span>
                     )}
                   </button>
                   
                   {showGroupsMenu && (
                     <div className="absolute right-0 top-9 bg-white dark:bg-[#111b21] opacity-100 border border-wa-border text-wa-text-primary rounded-lg shadow-2xl z-[101] w-52 py-2 text-xs font-semibold">
                       <div className="px-3 py-1.5 border-b border-wa-border text-wa-text-secondary uppercase tracking-wider text-[9px] font-bold">
                         WITHDRAW GROUPS - PENDING
                       </div>
                       {displayMBs.map(mb => {
                         const count = badges.groups_pending?.[mb.toLowerCase()] || badges.groups_pending?.[mb] || 0;
                         return (
                           <div 
                             key={mb}
                             onClick={async () => {
                               setShowGroupsMenu(false);
                               if (!permissions?.can_manage_groups && user?.role !== 'withdrawal_handler' && user?.role !== 'deposit_handler' && !isDepositHandler && user?.role !== 'super_admin' && user?.role !== 'admin' && !(user?.role === 'handler' && (user?.chat_type === 'withdrawal' || user?.chat_type === 'deposit'))) {
                                 toast.error("You do not have permission to manage group chats.");
                                 return;
                               }
                               try {
                                 const res = await axios.get(`${API}/groups/user-groups`);
                                 let gObj = res.data.find(gItem => gItem.name.toLowerCase().includes(mb.toLowerCase()));
                                 
                                 if (!gObj) {
                                   // Create group on-demand
                                   const createRes = await axios.post(`${API}/groups/create`, {
                                     name: `Withdraw Group ${mb}`,
                                     description: `Withdrawal request channel for ${mb}`
                                   });
                                   gObj = createRes.data;
                                 }
                                 
                                 if (gObj && (gObj.id || gObj.group_id)) {
                                   navigate(`/groups/${gObj.id || gObj.group_id}`);
                                 } else {
                                   toast.error(`Failed to navigate to group ${mb}`);
                                 }
                               } catch (e) {
                                 toast.error(`Failed to find or create group ${mb}`);
                               }
                             }}
                             className="px-3 py-2 hover:bg-wa-hover flex items-center justify-between cursor-pointer transition-colors text-left"
                           >
                             <span>{mb}</span>
                             {count > 0 ? (
                               <span className="bg-red-500 text-white rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                                 {count}
                               </span>
                             ) : (
                               <span className="text-wa-text-secondary text-[10px] font-normal">clear</span>
                             )}
                           </div>
                         );
                       })}
                     </div>
                   )}
                 </div>
                  )}

                 {/* PT Badge Dropdown */}
                 {(user?.role === 'super_admin' || user?.role === 'deposit_handler') && (
                   <div className="relative" ref={ptMenuRef}>
                     <button 
                       onClick={handleTogglePtMenu}
                       className="relative w-8 h-8 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center text-[10px] font-black shadow-md transition-all focus:outline-none cursor-pointer"
                      title="PT: Pending Tasks (No Staff Reply)"
                    >
                      <span>PT</span>
                      {badges.pt > 0 && (
                        <span className="absolute -bottom-1 -right-1 bg-red-600 text-white rounded-full text-[9px] font-black w-4 h-4 flex items-center justify-center border border-white shadow-sm">
                          {badges.pt}
                        </span>
                      )}
                    </button>

                    {showPtMenu && (
                      <div className="absolute right-[-72px] sm:right-[-72px] top-10 bg-white dark:bg-[#111b21] border border-gray-200 dark:border-gray-700/80 text-wa-text-primary rounded-2xl shadow-2xl z-[999] w-[310px] sm:w-[340px] overflow-hidden text-xs animate-in fade-in zoom-in-95 duration-100">
                        {/* Header matching N badge style */}
                        <div className="px-3.5 py-2.5 bg-slate-100/90 dark:bg-[#1f2c34] border-b border-gray-200 dark:border-gray-700/60 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                            <span className="text-[11px] font-black uppercase tracking-wider text-slate-800 dark:text-slate-100 truncate">
                              PENDING — AWAITING REPLY
                            </span>
                          </div>
                            {/* Dropdown Selector Trigger Button */}
                            <button
                              onClick={() => {
                                if (user?.role === 'super_admin') setShowPtThresholdMenu(!showPtThresholdMenu);
                              }}
                              disabled={user?.role === 'deposit_handler'}
                              className="text-[11px] font-extrabold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/40 hover:bg-amber-100 dark:hover:bg-amber-900/70 px-2.5 py-1 rounded-full border border-amber-200 dark:border-amber-800/60 transition-all flex items-center gap-1 cursor-pointer flex-shrink-0 shadow-2xs"
                            >
                              <span>{ptList.filter(item => (item.elapsed_minutes || 0) >= ptThreshold).length} · {ptThreshold}m+</span>
                              <CaretDown size={12} weight="bold" className={`transition-transform duration-200 ${showPtThresholdMenu ? 'rotate-180' : ''}`} />
                            </button>
                          </div>

                          {/* Threshold Selector Sub-Menu */}
                          {showPtThresholdMenu && user?.role === 'super_admin' && (
                            <div className="bg-slate-100/80 dark:bg-[#182229] border-b border-gray-200 dark:border-gray-700/60 p-3 space-y-2 animate-in fade-in slide-in-from-top-1 duration-150">
                              <div className="text-[10px] font-black tracking-wider text-slate-500 dark:text-slate-400 uppercase">
                                SHOW CHATS WAITING MORE THAN
                              </div>

                              <div className="grid grid-cols-4 gap-1.5">
                                {[
                                  { label: '0m', value: 0 },
                                  { label: '15m', value: 15 },
                                  { label: '30m', value: 30 },
                                  { label: '1h', value: 60 }
                                ].map((preset) => {
                                  const isActive = ptThreshold === preset.value;
                                  return (
                                    <button
                                      key={preset.value}
                                      onClick={() => {
                                        axios.put(`${API}/settings/pt-threshold`, { minutes: preset.value })
                                          .then((res) => setPtThreshold(Number(res.data?.minutes) || 0))
                                          .finally(() => setShowPtThresholdMenu(false));
                                      }}
                                      className={`py-1 px-2 rounded-full text-xs font-bold transition-all cursor-pointer border ${
                                        isActive
                                          ? 'bg-amber-500 text-white border-amber-600 shadow-2xs'
                                          : 'bg-white dark:bg-[#202c33] text-slate-700 dark:text-slate-200 border-gray-200 dark:border-gray-700 hover:bg-amber-50 dark:hover:bg-amber-950/40'
                                      }`}
                                    >
                                      {preset.label}
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Custom Minute Input */}
                              <div className="flex items-center gap-1.5 pt-1">
                                <input
                                  type="number"
                                  placeholder="Custom min"
                                  value={customPtMin}
                                  onChange={(e) => setCustomPtMin(e.target.value)}
                                  className="flex-1 px-2.5 py-1 bg-white dark:bg-[#202c33] border border-gray-300 dark:border-gray-700 rounded-full text-xs text-slate-900 dark:text-white outline-none focus:border-amber-500 font-medium"
                                />
                                <button
                                  onClick={() => {
                                    const parsed = parseInt(customPtMin, 10);
                                    if (!isNaN(parsed) && parsed >= 0 && parsed <= 1440) {
                                      axios.put(`${API}/settings/pt-threshold`, { minutes: parsed })
                                        .then((res) => setPtThreshold(Number(res.data?.minutes) || 0))
                                        .finally(() => setShowPtThresholdMenu(false));
                                    }
                                  }}
                                  className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-full transition-colors cursor-pointer shadow-2xs"
                                >
                                  Set
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Items List */}
                          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800/60">
                            {loadingPtList ? (
                              <div className="p-4 text-center text-gray-400 text-xs">Loading pending tasks...</div>
                            ) : ptList.filter(item => (item.elapsed_minutes || 0) >= ptThreshold).length === 0 ? (
                              <div className="p-6 text-center text-slate-400 dark:text-slate-500 text-xs italic flex flex-col items-center justify-center gap-1">
                                <span className="text-base font-bold text-emerald-500">✓</span>
                                <span>All caught up</span>
                              </div>
                            ) : (
                              ptList
                                .filter(item => (item.elapsed_minutes || 0) >= ptThreshold)
                                .slice(0, 10)
                                .map((item, idx) => (
                                  <div
                                    key={item.room_id || idx}
                                    onClick={() => {
                                      setShowPtMenu(false);
                                      setSelectedChatId(item.room_id);
                                    }}
                                    className="p-2.5 hover:bg-slate-50 dark:hover:bg-[#182229] flex items-center justify-between gap-2 transition-colors cursor-pointer"
                                  >
                                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                      <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 font-extrabold text-[11px] flex items-center justify-center border border-amber-200 dark:border-amber-800/60 flex-shrink-0">
                                        {idx + 1}
                                      </div>
                                      
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="text-xs font-bold text-slate-900 dark:text-white truncate">
                                            {item.customer_name}
                                          </span>
                                          {item.mb_setup && (
                                            <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-[9px] font-black rounded border border-amber-200 dark:border-amber-700/50">
                                              {item.mb_setup}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[10px] text-slate-400 dark:text-slate-400 truncate mt-0.5 italic">
                                          {item.last_message_text || 'No message text'}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="flex-shrink-0 text-right">
                                      <span className="text-[10px] font-black text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800/60">
                                        {item.elapsed_minutes >= 60
                                          ? `${Math.floor(item.elapsed_minutes / 60)}h ${item.elapsed_minutes % 60}m`
                                          : `${item.elapsed_minutes}m`}
                                      </span>
                                    </div>
                                  </div>
                                ))
                            )}
                          </div>

                          {/* Bottom View All Button matching N badge style */}
                          <div className="p-2 bg-slate-100/90 dark:bg-[#1f2c34] border-t border-gray-200 dark:border-gray-700/60 flex items-center justify-center">
                            <button
                              onClick={() => {
                                setShowPtMenu(false);
                                setActiveBadgeView('pt');
                              }}
                              className="w-full py-1.5 text-xs font-extrabold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/40 hover:bg-amber-100 dark:hover:bg-amber-900/70 px-3 rounded-full border border-amber-200 dark:border-amber-800/60 transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                            >
                              View all <span className="font-black text-amber-700 dark:text-amber-300">({ptList.filter(item => (item.elapsed_minutes || 0) >= ptThreshold).length})</span>
                            </button>
                          </div>
                        </div>
                      )}
                   </div>
                 )}
              </div>
            )}

            <ThemeToggleButton size={22} />
            <div ref={menuRef} className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-green-600 rounded-full"
              >
                <DotsThreeVertical size={24} weight="bold" />
              </button>

              {/* Dropdown Menu */}
              {showMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#111b21] opacity-100 rounded-lg shadow-lg py-2 z-[500] border border-wa-border">
                  {/* Menu items - available to all handlers */}
                  <>
                      {user?.role !== 'calling_handler' && (
                        <button
                          onClick={() => {
                            navigate('/groups');
                            setShowMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-wa-text-primary hover:bg-wa-hover flex items-center gap-2"
                        >
                          <UsersThree size={20} />
                          Groups
                        </button>
                      )}
                      {permissions?.can_send_broadcast && (
                        <button
                          onClick={() => {
                            navigate('/broadcast');
                            setShowMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-wa-text-primary hover:bg-wa-hover flex items-center gap-2"
                        >
                          <Megaphone size={20} />
                          Broadcast Message
                        </button>
                      )}
                      {user?.role !== 'calling_handler' && canAccessPower('client_list') && (
                        <button
                          onClick={() => {
                            navigate('/client-list');
                            setShowMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-wa-text-primary hover:bg-wa-hover flex items-center gap-2"
                        >
                          <Users size={20} />
                          Client List
                        </button>
                      )}
                  </>
                  <button
                    onClick={() => {
                      handleRefresh();
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-blue-600 hover:bg-wa-hover flex items-center gap-2"
                  >
                    <ArrowClockwise size={20} />
                    Refresh
                  </button>
                  <button
                    onClick={() => {
                      navigate('/handler-settings');
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-wa-text-primary hover:bg-wa-hover flex items-center gap-2"
                  >
                    <Gear size={20} />
                    Settings
                  </button>
                  <div className="border-t my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-wa-hover flex items-center gap-2"
                  >
                    <SignOut size={20} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Body Layout */}
      <div className="flex-1 flex flex-row overflow-hidden relative w-full">
        {/* Left Panel - Sidebar */}
        <div 
          style={isDesktop ? { width: `${sidebarWidth}px` } : undefined}
          className={`flex flex-col ${isDesktop ? 'border-r border-wa-border flex-shrink-0 z-10' : 'flex-1'} bg-wa-panel h-full overflow-hidden`}
        >

      {/* Support Sub-tabs: Support Normal / Inquiry Support (Hidden for calling_handler) */}
      {user?.role !== 'calling_handler' && ((isDepositHandler && chatTypeTab === 'support') || user?.chat_type === 'support' || user?.role === 'support_handler') && (
        <div className="bg-wa-panel border-b flex flex-shrink-0">
          <button
            onClick={() => setSupportSubTab('support')}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${
              supportSubTab === 'support'
                ? 'text-purple-600 border-b-2 border-purple-600 bg-wa-hover'
                : 'text-wa-text-secondary hover:text-wa-text-primary'
            }`}
            data-testid="handler-support-normal-tab"
          >
            Support Normal
          </button>
          <button
            onClick={() => {
              setSupportSubTab('inquiry');
              axios.get(`${API}/auth/me`).then(res => {
                if (res.data?.user?.settings_powers) {
                  setLivePowers(res.data.user.settings_powers);
                  const savedUser = localStorage.getItem('dws_user');
                  if (savedUser) {
                    const parsed = JSON.parse(savedUser);
                    parsed.settings_powers = res.data.user.settings_powers;
                    localStorage.setItem('dws_user', JSON.stringify(parsed));
                  }
                }
              }).catch(() => {});
            }}
            className={`flex-1 py-2 text-xs font-semibold transition-colors ${
              supportSubTab === 'inquiry'
                ? 'text-orange-600 border-b-2 border-orange-600 bg-wa-hover'
                : 'text-wa-text-secondary hover:text-wa-text-primary'
            }`}
            data-testid="handler-inquiry-support-tab"
          >
            Inquiry Support
          </button>
        </div>
      )}

      {/* Filter Pills */}
      <div className="bg-wa-panel border-b flex gap-2 px-3 py-2 overflow-x-auto flex-shrink-0 scrollbar-hide no-scrollbar">
        <button
          onClick={() => setActiveTab('all')}
          className={`text-[13px] px-3 py-[5px] rounded-full whitespace-nowrap transition-colors ${
            activeTab === 'all' ? 'bg-[#e7fce3] text-[#008069] font-medium' : 'bg-wa-panel-header text-wa-icon hover:bg-[#e9edef]'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setActiveTab('unread')}
          className={`text-[13px] px-3 py-[5px] rounded-full whitespace-nowrap flex items-center gap-1 transition-colors ${
            activeTab === 'unread' ? 'bg-[#e7fce3] text-[#008069] font-medium' : 'bg-wa-panel-header text-wa-icon hover:bg-[#e9edef]'
          }`}
        >
          Unread {unreadCount > 0 && <span className="bg-[#008069]/10 text-[#008069] px-1.5 py-0.5 rounded-full text-[10px] font-bold">{unreadCount}</span>}
        </button>
        <button
          onClick={() => setActiveTab('favourites')}
          className={`text-[13px] px-3 py-[5px] rounded-full whitespace-nowrap transition-colors ${
            activeTab === 'favourites' ? 'bg-[#e7fce3] text-[#008069] font-medium' : 'bg-wa-panel-header text-wa-icon hover:bg-[#e9edef]'
          }`}
        >
          Favourites
        </button>
        {user?.role !== 'calling_handler' && (
          <button
            onClick={() => setActiveTab('groups')}
            className={`text-[13px] px-3 py-[5px] rounded-full whitespace-nowrap transition-colors ${
              activeTab === 'groups' ? 'bg-[#e7fce3] text-[#008069] font-medium' : 'bg-wa-panel-header text-wa-icon hover:bg-[#e9edef]'
            }`}
          >
            Groups
          </button>
        )}
        <button
          onClick={() => setActiveTab('archive')}
          className={`text-[13px] px-3 py-[5px] rounded-full whitespace-nowrap flex items-center gap-1.5 transition-colors ${
            activeTab === 'archive' ? 'bg-[#e7fce3] text-[#008069] font-medium' : 'bg-wa-panel-header text-wa-icon hover:bg-[#e9edef]'
          }`}
        >
          Archived {chats.filter(r => r.is_archived).length > 0 && <span className="bg-[#008069]/10 text-[#008069] px-1.5 py-0.5 rounded-full text-[10px] font-bold">{chats.filter(r => r.is_archived).length}</span>}
        </button>
      </div>

      {/* Search Bar & D / W Quick Switching Badges */}
      <div className="bg-wa-hover p-2.5 border-b flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-xs bg-white dark:bg-[#111b21] text-slate-900 dark:text-white"
          />
          <MagnifyingGlass size={16} className="absolute left-2.5 top-2.5 text-wa-text-secondary" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-2 text-wa-text-secondary hover:text-wa-text-secondary"
            >
              ✕
            </button>
          )}
        </div>

        {/* D / W Quick Switch Buttons (Scoped strictly by role) */}
        {isDepositHandler && chatTypeTab === 'deposit' && (
          <button
            onClick={() => { setChatTypeTab('withdrawal'); setMbFilter('all'); }}
            className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white font-black text-xs flex items-center justify-center flex-shrink-0 shadow-md transition-all cursor-pointer"
            title="Switch to Withdrawal Panel"
          >
            W
          </button>
        )}
        {isDepositHandler && chatTypeTab === 'withdrawal' && (
          <button
            onClick={() => { setChatTypeTab('deposit'); setMbFilter('all'); }}
            className="w-8 h-8 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs flex items-center justify-center flex-shrink-0 shadow-md transition-all cursor-pointer"
            title="Switch to Deposit Panel"
          >
            D
          </button>
        )}
        {isDepositHandler && chatTypeTab === 'support' && user?.role !== 'support_handler' && user?.role !== 'inquiry_handler' && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => { setChatTypeTab('deposit'); setMbFilter('all'); }}
              className="w-7 h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[11px] flex items-center justify-center shadow-md transition-all cursor-pointer"
              title="Switch to Deposit Panel"
            >
              D
            </button>
            <button
              onClick={() => { setChatTypeTab('withdrawal'); setMbFilter('all'); }}
              className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white font-black text-[11px] flex items-center justify-center shadow-md transition-all cursor-pointer"
              title="Switch to Withdrawal Panel"
            >
              W
            </button>
          </div>
        )}
      </div>

      {/* MB Setup Dropdown — visible in Deposit / Withdrawal / Support views */}
      {!isInquiryHandler && activeTab !== 'groups' && (['deposit', 'withdrawal', 'support'].includes(user?.chat_type || '') || (isDepositHandler && ['deposit', 'withdrawal', 'support'].includes(chatTypeTab))) && (
        <div className="bg-wa-panel border-b border-blue-100 flex items-center justify-between gap-2 px-3 py-2 flex-shrink-0 relative z-10" data-testid="mb-tabs-handler">
          <div className="relative" ref={mbDropdownRef}>
            <button
              onClick={() => setShowMbDropdown(!showMbDropdown)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-[12px] px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
              data-testid="mb-tab-all-dropdown-handler"
            >
              <span>
                {mbFilter === 'all'
                  ? `All Setups (${chats.filter(c => {
                      const mb = c.mb_setup || c.customer?.mb_setup;
                      return mb && mb !== 'N' && mb !== 'Unassigned';
                    }).length})`
                  : `${mbFilter} (${chats.filter(c => (c.mb_setup || c.customer?.mb_setup) === mbFilter).length})`}
              </span>
              <CaretDown size={14} weight="bold" className={`transition-transform duration-200 ${showMbDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showMbDropdown && (
              <div className="absolute top-9 left-0 bg-white dark:bg-[#111b21] border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-[999] w-56 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                <button
                  onClick={() => { setMbFilter('all'); setShowMbDropdown(false); }}
                  className={`w-full px-3.5 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between border-b border-gray-100 dark:border-gray-800 ${mbFilter === 'all' ? 'font-bold text-blue-600' : ''}`}
                >
                  <span>All Setups</span>
                  <span className="bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full font-bold text-[11px]">
                    {chats.filter(c => {
                      const mb = c.mb_setup || c.customer?.mb_setup;
                      return mb && mb !== 'N' && mb !== 'Unassigned';
                    }).length}
                  </span>
                </button>
                <div className="max-h-60 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/40">
                  {displayMBs.map((mb) => {
                    const bucket = chats.filter(c => (c.mb_setup || c.customer?.mb_setup) === mb);
                    const count = bucket.length;
                    const unread = bucket.reduce((s, c) => s + (c.unread_count || 0), 0);
                    const isActive = mbFilter === mb;
                    return (
                      <button
                        key={mb}
                        onClick={() => { setMbFilter(mb); setShowMbDropdown(false); }}
                        className={`w-full px-3.5 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between transition-colors ${isActive ? 'bg-blue-50/70 dark:bg-blue-950/40 font-bold text-blue-600' : ''}`}
                        data-testid={`mb-tab-${mb}`}
                      >
                        <span className="truncate">{mb}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">({count})</span>
                          {unread > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white">
                              {unread > 99 ? '99+' : unread}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>


        </div>
      )}
      {showBulkMB && (
        <BulkAssignMBModal
          open={showBulkMB}
          onClose={() => setShowBulkMB(false)}
          clients={chats.map(c => ({
            id: c.customer?.id,
            name: c.customer?.name,
            phone: c.customer?.phone,
            mb_setup: c.mb_setup || c.customer?.mb_setup,
            unread_count: c.unread_count || 0,
          }))}
          actorId={user?.id}
          onDone={(ids, mbValue) => {
            // Optimistic update: patch mb_setup in local chats
            setChats(prev => prev.map(c => (
              ids.includes(c.customer?.id)
                ? { ...c, customer: { ...c.customer, mb_setup: mbValue } }
                : c
            )));
          }}
        />
      )}

      {/* Main Content Area */}
      {bottomTab === 'updates' ? (
        /* Updates Tab Content - Status + Broadcast */
        <div className="flex-1 overflow-y-auto bg-wa-hover">
          <div className="max-w-2xl mx-auto p-4">
            <h2 className="text-xl font-bold text-wa-text-primary mb-4">Updates</h2>
            
            {/* Status Management (Inline) */}
            <div className="mb-4">
              <StatusManagement user={user} />
            </div>

            {/* Broadcast Section */}
            {permissions?.can_send_broadcast && (
              <div className="bg-wa-panel rounded-xl shadow-sm">
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-wa-text-primary">Broadcast</h3>
                </div>
                <div 
                  className="p-4 cursor-pointer hover:bg-wa-hover transition-colors"
                  onClick={() => navigate('/broadcast')}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-wa-hover flex items-center justify-center">
                      <Megaphone size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-wa-text-primary">Broadcast Messages</p>
                      <p className="text-sm text-wa-text-secondary">Send message to multiple clients</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : bottomTab === 'settings' ? (
        /* Admin-Styled Handler Settings Tab Content */
        <div className="flex-1 overflow-y-auto bg-slate-50 min-h-screen">
          {/* Active Sub-Setting View */}
          {activeSubSetting === 'calling_agent_reports' ? (
            <CallingAgentReports onBack={() => setActiveSubSetting(null)} />
          ) : activeSubSetting === 'create_client' ? (
            <CreateClient onBack={() => setActiveSubSetting(null)} />
          ) : activeSubSetting === 'reports' ? (
            <div className="h-screen w-screen bg-white fixed inset-0 z-[100] p-1.5 md:p-3 overflow-hidden">
              <Reports onClose={() => setActiveSubSetting(null)} onBack={() => setActiveSubSetting(null)} />
            </div>
          ) : activeSubSetting === 'client_directory' ? (
            <ClientsDirectory onBack={() => setActiveSubSetting(null)} />
          ) : activeSubSetting === 'appearance' ? (
            <Appearance onBack={() => setActiveSubSetting(null)} />
          ) : activeSubSetting === 'service_timelines' ? (
            <ServiceTimelines onBack={() => setActiveSubSetting(null)} />
          ) : activeSubSetting === 'status' ? (
            <div className="p-4 max-w-4xl mx-auto space-y-4">
              <button
                onClick={() => setActiveSubSetting(null)}
                className="flex items-center gap-2 text-xs font-bold text-slate-700 hover:text-slate-900 bg-white border border-slate-200 px-3 py-2 rounded-xl shadow-2xs transition-colors"
              >
                <ArrowLeft size={16} weight="bold" /> Back to Settings
              </button>
              <StatusManagement user={user} />
            </div>
          ) : activeSubSetting === 'blocked_clients' ? (
            <div className="p-4 max-w-4xl mx-auto space-y-4">
              <button
                onClick={() => setActiveSubSetting(null)}
                className="flex items-center gap-2 text-xs font-bold text-slate-700 hover:text-slate-900 bg-white border border-slate-200 px-3 py-2 rounded-xl shadow-2xs transition-colors"
              >
                <ArrowLeft size={16} weight="bold" /> Back to Settings
              </button>
              <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Blocked Clients</h3>
                <BlockedClientsSection userId={user.id} userRole={user.role} />
              </div>
            </div>
          ) : activeSubSetting === 'inquiry_chats' ? (
            <div className="p-4 max-w-4xl mx-auto space-y-4">
              <button
                onClick={() => setActiveSubSetting(null)}
                className="flex items-center gap-2 text-xs font-bold text-slate-700 hover:text-slate-900 bg-white border border-slate-200 px-3 py-2 rounded-xl shadow-2xs transition-colors"
              >
                <ArrowLeft size={16} weight="bold" /> Back to Settings
              </button>
              <div className="bg-white rounded-xl shadow-xs border border-slate-200 p-4">
                <h3 className="font-bold text-base text-slate-900 mb-3 flex items-center gap-2">
                  <ChatCenteredDots size={20} className="text-orange-500" /> Inquiry Chats
                </h3>
                {inquiryChats.length === 0 ? (
                  <p className="p-4 text-center text-slate-500 text-sm">No inquiry chats found</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {inquiryChats.map((chat) => (
                      <div
                        key={chat.room_id}
                        onClick={() => {
                          if (isDesktop) {
                            setSelectedChatId(chat.room_id);
                          } else {
                            navigate(`/handler-chat/${chat.room_id}`, { state: { chatTypeTab, supportSubTab, mbFilter } });
                          }
                        }}
                        className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer rounded-lg transition-colors"
                      >
                        <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-orange-600 font-bold text-sm">
                            {(chat.customer?.name || 'I')[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 truncate text-sm">{chat.customer?.name || 'Inquiry Client'}</h4>
                          <p className="text-xs text-slate-500 truncate">{chat.last_message?.text || 'No messages'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Main Settings Overview Cards Grid */
            <div>
              {/* Header Banner */}
              <div className="bg-[#00b87c] text-white px-6 py-4 flex items-center justify-between shadow-md">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setBottomTab('chats')}
                    className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                    title="Back to chats"
                  >
                    <ArrowLeft size={22} weight="bold" />
                  </button>
                  <div>
                    <h1 className="text-lg font-bold leading-tight">Handler Settings</h1>
                    <p className="text-xs text-emerald-100 font-medium">Control Center · Admin Granted Access</p>
                  </div>
                </div>
              </div>

              <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
                {/* Search Settings Bar */}
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search settings..."
                    value={settingsSearch}
                    onChange={(e) => setSettingsSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00b87c] shadow-2xs transition-all placeholder-slate-400"
                  />
                </div>

                {/* Filter Cards based on user.settings_powers */}
                {(() => {
                  const userPowers = { ...DEFAULT_SETTINGS_POWERS, ...(user?.settings_powers || {}) };
                  const visibleCards = HANDLER_SETTINGS_CARDS.filter(card => {
                    if (user?.role === 'calling_handler') {
                      return card.id === 'inactive_clients' || card.id === 'calling_agent_reports';
                    }
                    return userPowers[card.powerKey] !== false;
                  }).filter(card => {
                    if (!settingsSearch || !settingsSearch.trim()) return true;
                    const q = settingsSearch.trim().toLowerCase();
                    return card.title.toLowerCase().includes(q) || card.desc.toLowerCase().includes(q);
                  });

                  if (visibleCards.length === 0) {
                    return (
                      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 space-y-2">
                        <Gear size={36} className="mx-auto text-slate-300" weight="light" />
                        <p className="font-bold text-sm text-slate-700">No settings menu available</p>
                        <p className="text-xs text-slate-400">Settings items are assigned per handler by Admin</p>
                      </div>
                    );
                  }

                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {visibleCards.map((card) => {
                        const IconComp = card.icon;
                        return (
                          <div
                            key={card.id}
                            onClick={() => {
                              if (card.id === 'inactive_clients') {
                                navigate('/inactive-clients');
                              } else if (card.id === 'inquiry_chats') {
                                loadInquiryChats();
                                setActiveSubSetting('inquiry_chats');
                              } else {
                                setActiveSubSetting(card.id);
                              }
                            }}
                            className="bg-white border border-slate-200/80 hover:border-[#00b87c] p-4 rounded-xl shadow-2xs hover:shadow-md cursor-pointer transition-all flex items-center justify-between group select-none"
                          >
                            <div className="flex items-center gap-3.5">
                              <div className={`p-2.5 ${card.bg} ${card.color} rounded-xl flex-shrink-0 transition-transform group-hover:scale-105`}>
                                <IconComp size={22} weight="bold" />
                              </div>
                              <div>
                                <h3 className="font-bold text-slate-800 text-sm group-hover:text-[#00b87c] transition-colors">
                                  {card.title}
                                </h3>
                                <p className="text-xs text-slate-500 leading-tight mt-0.5">
                                  {card.desc}
                                </p>
                              </div>
                            </div>
                            <CaretRight size={18} className="text-slate-300 group-hover:text-[#00b87c] group-hover:translate-x-0.5 transition-all" weight="bold" />
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Chat List */
        <div ref={inquiryListRef} onScroll={handleInquiryListScroll} className="flex-1 overflow-y-auto">
          {/* Selection Mode Header */}
          {selectionMode && (
            <div className="sticky top-0 bg-green-500 text-white p-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <button onClick={cancelSelection} className="hover:bg-green-600 p-1 rounded">
                  ← Back
                </button>
                <span className="font-semibold">{selectedRooms.length} selected</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleToggleArchive}
                  disabled={selectedRooms.length === 0}
                  className="bg-green-700 hover:bg-green-800 px-3 py-1 rounded disabled:opacity-50 text-sm flex items-center gap-1"
                >
                  📦 {activeTab === 'archive' ? 'Unarchive' : 'Archive'}
                </button>
                {permissions?.can_delete_chat && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={selectedRooms.length === 0}
                    className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded disabled:opacity-50 text-sm"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          )}



          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-wa-text-secondary">
              <div className="w-8 h-8 border-2 border-wa-border border-t-blue-400 rounded-full animate-spin" />
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-wa-text-secondary">
              <Chats size={64} weight="light" />
              <p className="mt-4">No chats found</p>
            </div>
          ) : (
            (activeTab === 'groups'
              ? filteredChats
              : filteredChats.slice(0, visibleChatsCount)
            ).map((chat) => {
              const isGroup = activeTab === 'groups';
              const itemId = isGroup ? chat.id : chat.room_id;
              const itemName = isGroup ? chat.name : chat.customer?.name;
              const itemProfilePic = isGroup ? chat.profile_pic : chat.customer?.profile_pic;
              
              return (
              <div
                key={itemId}
                onClick={() => {
                  if (selectionMode) {
                    toggleRoomSelection(itemId);
                  } else {
                    if (isGroup) {
                      handleGroupClick(chat.id);
                    } else {
                      handleChatClick(chat);
                    }
                  }
                }}
                onTouchStart={(e) => !selectionMode && handleLongPressStart(itemId, e)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleLongPressEnd}
                onMouseDown={(e) => !selectionMode && handleLongPressStart(itemId, e)}
                onMouseMove={handleTouchMove}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                className={`flex items-center gap-3 p-3 hover:bg-wa-hover cursor-pointer border-b border-wa-border ${
                  selectedRooms.includes(itemId) ? 'bg-wa-hover' : ''
                }`}
              >
                {/* Selection Checkbox */}
                {selectionMode && (
                  <div className="flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={selectedRooms.includes(itemId)}
                      onChange={() => toggleRoomSelection(itemId)}
                      className="w-5 h-5 text-green-600"
                    />
                  </div>
                )}

                {/* Profile Picture - WhatsApp colored placeholders */}
                <div
                  className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center"
                  style={itemProfilePic ? {
                    backgroundImage: `url(${itemProfilePic.startsWith('http') ? itemProfilePic : API + itemProfilePic})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: 'transparent',
                  } : {
                    backgroundColor: (() => {
                      const colors = ['#00a884', '#53bdeb', '#e67e22', '#9b59b6', '#e74c3c', '#2ecc71', '#f39c12', '#1abc9c'];
                      const name = itemName || 'C';
                      return colors[name.charCodeAt(0) % colors.length];
                    })(),
                  }}
                >
              {!itemProfilePic && (
                    <span className="text-lg font-bold text-white">
                      {(itemName || 'C')[0]?.toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Chat Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <h3 className="font-semibold text-wa-text-primary truncate flex items-center gap-1">
                        {itemName || (isGroup ? 'Group' : 'Customer')}
                        {!isGroup && chat.customer?.is_suspicious && (
                          <span className="inline-flex items-center gap-0" title="Suspicious Customer">
                            <Flag size={12} weight="fill" className="text-red-500" />
                            <Flag size={12} weight="fill" className="text-red-500" />
                            <Flag size={12} weight="fill" className="text-red-500" />
                          </span>
                        )}
                        {!isGroup && chat.customer?.is_vip && (
                          <span className="inline-flex items-center gap-0" title="VIP Customer">
                            <Flag size={12} weight="fill" className="text-green-500" />
                            <Flag size={12} weight="fill" className="text-green-500" />
                            <Flag size={12} weight="fill" className="text-green-500" />
                          </span>
                        )}
                      </h3>
                      {!isGroup && <MBSetupBadge value={chat.mb_setup || chat.customer?.mb_setup} />}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Copy Details Button */}
                      {!isGroup && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCopyPopupChat(chat);
                          }}
                          className="p-0.5 hover:bg-wa-hover rounded transition-colors"
                          title="Copy details"
                        >
                          <Copy size={18} className="text-wa-text-secondary hover:text-blue-500" />
                        </button>
                      )}
                      {/* WhatsApp Button */}
                      {!isGroup && chat.customer?.phone && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openWhatsApp(chat.customer.phone, chat.room_id, chat.customer?.id);
                          }}
                          className="p-0.5 hover:bg-wa-hover rounded transition-colors"
                          title="Open WhatsApp"
                        >
                          <WhatsappLogo size={18} className="text-wa-text-secondary hover:text-green-500" />
                        </button>
                      )}
                      <span className="text-xs text-wa-text-secondary">
                        {formatTime(isGroup ? chat.last_message_at : chat.last_message?.timestamp)}
                      </span>
                      {!isGroup && ((unreadCounts[chat.room_id] ?? chat.unread_count) > 0) && (
                        <div className="bg-green-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                          {(unreadCounts[chat.room_id] ?? chat.unread_count) > 99 ? '99+' : (unreadCounts[chat.room_id] ?? chat.unread_count)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-wa-text-secondary truncate flex-1 min-w-0">
                      {isGroup ? (
                        chat.description || 'Group chat'
                      ) : (
                        (typeof chat.last_message === 'object' ? chat.last_message?.text : chat.last_message) || chat.customer?.phone || 'No messages yet'
                      )}
                    </p>
                    {!isGroup && (Number(chat.pending_forms_count) > 0 || chat.withdrawal_action_pending === true) && (
                      <span className="ml-2 px-2 py-0.5 rounded-full bg-[#f59e0b] text-white font-bold text-[10px] flex items-center gap-1 flex-shrink-0 shadow-sm">
                        <span>🎫</span> {chat.pending_forms_count || 1}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              );
            })
          )}
          {visibleChatsCount < filteredChats.length && activeTab !== 'groups' && (
            <div className="p-3 text-center text-xs text-wa-text-secondary flex items-center justify-center gap-2">
              <div className="w-3.5 h-3.5 border-2 border-wa-border border-t-emerald-500 rounded-full animate-spin" />
              <span>Loading more chats…</span>
            </div>
          )}
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="bg-wa-panel border-t flex justify-between py-1.5 px-1 flex-shrink-0">
        <button
          onClick={() => setBottomTab('chats')}
          className={`flex flex-col items-center py-1 px-2 min-w-0 ${
            bottomTab === 'chats' ? 'text-green-600' : 'text-wa-text-secondary'
          }`}
        >
          <Chats size={20} weight={bottomTab === 'chats' ? 'fill' : 'regular'} />
          <span className="text-[10px] mt-0.5">Chats</span>
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1/4 bg-green-500 text-white text-xs font-semibold px-1.5 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
        
        {/* Groups, Broadcast, Updates, Clients - available to all handlers */}
        <>
            <button
              onClick={() => setBottomTab('updates')}
              className={`flex flex-col items-center py-1 px-2 min-w-0 ${
                bottomTab === 'updates' ? 'text-green-600' : 'text-wa-text-secondary'
              }`}
              data-testid="updates-tab"
            >
              <Megaphone size={20} weight={bottomTab === 'updates' ? 'fill' : 'regular'} />
              <span className="text-[10px] mt-0.5">Updates</span>
            </button>
            
            {user?.role !== 'calling_handler' && (
              <button
                onClick={() => navigate('/groups')}
                className="flex flex-col items-center py-1 px-2 min-w-0 text-wa-text-secondary hover:text-green-600"
              >
                <UsersThree size={20} />
                <span className="text-[10px] mt-0.5">Groups</span>
              </button>
            )}
            
            {user?.role !== 'calling_handler' && canAccessPower('client_list') && (
              <button
                onClick={() => navigate('/client-list')}
                className={`flex flex-col items-center py-1 px-2 min-w-0 ${
                  bottomTab === 'clients' ? 'text-green-600' : 'text-wa-text-secondary'
                }`}
              >
                <Users size={20} weight={bottomTab === 'clients' ? 'fill' : 'regular'} />
                <span className="text-[10px] mt-0.5">Clients</span>
              </button>
            )}
        </>
        
        <button
          onClick={() => navigate('/handler-settings')}
          className="flex flex-col items-center py-1 px-2 min-w-0 text-wa-text-secondary hover:text-green-600"
        >
          <Gear size={20} />
          <span className="text-[10px] mt-0.5">Settings</span>
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Delete Chats?</h3>
            <p className="text-wa-text-secondary mb-4">
              Are you sure you want to delete {selectedRooms.length} chat{selectedRooms.length > 1 ? 's' : ''}? 
              This will only remove {selectedRooms.length > 1 ? 'them' : 'it'} from your view. 
              The customer and admin will still see {selectedRooms.length > 1 ? 'these chats' : 'this chat'}.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-wa-hover"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRooms}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Details Popup - Login + Bank */}
      {copyPopupChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCopyPopupChat(null)}>
          <div className="bg-wa-panel rounded-xl max-w-sm w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="copy-details-popup">
            <h3 className="text-lg font-bold text-wa-text-primary mb-4">Copy Details</h3>
            
            {/* Login Details */}
            <div className="bg-wa-hover rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-wa-text-primary">Login Details</span>
                <button
                  onClick={async () => {
                    const c = copyPopupChat.customer || copyPopupChat;
                    const hasFullPhoneAccess = canAccessPower('mobile_number_full');
                    const phoneVal = maskPhone(c.phone || c.mobile_number, hasFullPhoneAccess) || 'N/A';
                    let text = `Username: ${c.username || c.name || 'N/A'}\nPhone: ${phoneVal}\nPassword: Abcd1234`;
                    try {
                      const settingsRes = await axios.get(`${API}/settings`);
                      const loginLink = settingsRes.data?.customer_login_link;
                      if (loginLink) text += `\nLink: ${loginLink}`;
                    } catch (e) {}
                    navigator.clipboard.writeText(text);
                    toast.success('Login details copied!', { duration: 1500 });
                    setCopyPopupChat(null);
                  }}
                  className="px-3 py-1 bg-[#00a884] text-white text-xs rounded-full hover:bg-[#008f72]"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-wa-text-secondary">Username: {(copyPopupChat.customer || copyPopupChat).username || (copyPopupChat.customer || copyPopupChat).name || 'N/A'}</p>
              <p className="text-xs text-wa-text-secondary">Phone: {maskPhone((copyPopupChat.customer || copyPopupChat).phone || (copyPopupChat.customer || copyPopupChat).mobile_number, canAccessPower('mobile_number_full')) || 'N/A'}</p>
              <p className="text-xs text-wa-text-secondary">Password: Abcd1234</p>
            </div>

            {/* Bank Details - Fetch from registered clients */}
            <div className="bg-wa-hover rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-wa-text-primary">Bank Details</span>
                <button
                  onClick={async () => {
                    try {
                      const customerId = (copyPopupChat.customer || copyPopupChat).id || copyPopupChat.customer_id;
                      const res = await axios.get(`${API}/clients/registered`);
                      const client = res.data.find(c => c.id === customerId || c.phone === (copyPopupChat.customer || copyPopupChat).phone);
                      if (!client || (!client.bank_name && !client.bank_account_number)) {
                        toast.error('No bank details found'); return;
                      }
                      let text = `User Name: ${client.username || 'N/A'}

A/c Holder Name: ${client.account_holder_name || client.client_name || 'N/A'}

Bank Account No: ${client.bank_account_number || 'N/A'}

IFSC Code: ${client.ifsc_code || 'N/A'}

Bank Name: ${client.bank_name || 'N/A'}`;
                      if (client.address) text += `\n\nAddress: ${client.address}`;
                      if (client.upi_id) text += `\n\nUPI ID: ${client.upi_id}`;
                      navigator.clipboard.writeText(text);
                      toast.success('Bank details copied!', { duration: 1500 });
                      setCopyPopupChat(null);
                    } catch (e) {
                      toast.error('Failed to fetch bank details');
                    }
                  }}
                  className="px-3 py-1 bg-[#00a884] text-white text-xs rounded-full hover:bg-[#008f72]"
                >
                  Copy
                </button>
              </div>
              <CopyPopupBankPreview customerId={(copyPopupChat.customer || copyPopupChat).id || copyPopupChat.customer_id} phone={(copyPopupChat.customer || copyPopupChat).phone} />
            </div>

            <button
              onClick={() => setCopyPopupChat(null)}
              className="w-full py-2.5 border border-wa-border rounded-lg text-wa-text-primary hover:bg-wa-hover"
            >
              Close
            </button>
          </div>
        </div>
      )}

      </div>{/* End left panel */}

      {/* Resize handle between list and chat (Desktop only) */}
      {isDesktop && (
        <ResizeHandle onMouseDown={startDragging} isDragging={isDragging} />
      )}

      {/* Right Panel - Active Chat & Right Contact Profile Drawer (Desktop Split View) */}
      {isDesktop && (
        <div className="flex-1 h-full relative overflow-hidden bg-[#f0f2f5] dark:bg-[#111b21] flex flex-row">
          <div className="flex-1 h-full relative overflow-hidden flex flex-col">
            {selectedChatId ? (
              <HandlerChat
                key={selectedChatId}
                embeddedRoomId={selectedChatId}
                onBackToList={() => setSelectedChatId(null)}
                onOpenProfile={(customerId, roomId) => {
                  setActiveProfileCustomerId(customerId);
                  setActiveProfileRoomId(roomId);
                }}
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-8 text-center border-b-[6px] border-[#25d366]">
                <div className="w-24 h-24 mb-6 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center shadow-inner">
                  <img src="/icons/icon-128x128.png" alt="DWS Logo" className="w-16 h-16 object-contain opacity-80" />
                </div>
                <h2 className="text-2xl font-light text-slate-700 dark:text-slate-200 mb-2">DWS Vault Chat</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
                  Select a chat to start messaging
                </p>
              </div>
            )}
          </div>

          {/* WhatsApp Web Style Right Side Contact Profile Panel */}
          {activeProfileCustomerId && (
            <div className="w-full sm:w-[380px] md:w-[420px] lg:w-[450px] h-full border-l border-wa-border bg-wa-panel flex-shrink-0 z-50 animate-in slide-in-from-right duration-200">
              <HandlerProfile
                customerId={activeProfileCustomerId}
                roomId={activeProfileRoomId}
                onClose={() => {
                  setActiveProfileCustomerId(null);
                  setActiveProfileRoomId(null);
                }}
                isEmbedded={true}
              />
            </div>
          )}
        </div>
      )}
      </div>{/* End Main Body Layout */}
      {/* Badge Details Dashboard Overlay */}
      {activeBadgeView && (
        <BadgeDetailsView
          badgeType={activeBadgeView}
          API={API}
          onClose={() => setActiveBadgeView(null)}
          onOpenChat={(roomId) => {
            if (isDesktop) {
              setSelectedChatId(roomId);
            } else {
              navigate(`/handler-chat/${roomId}`, { state: { chatTypeTab, supportSubTab, mbFilter } });
            }
          }}
        />
      )}

      {showReports && (
        <div className="h-screen w-screen bg-white fixed inset-0 z-[100] p-1.5 md:p-3 overflow-hidden">
          <Reports onClose={() => setShowReports(false)} />
        </div>
      )}

    </div>
  );
};

export default HandlerDashboard;
